import {
  Container,
  BackWrapper,
  Title,
  RowPaddingBottom,
  CategoriesContainer,
  SupplierCompanyName,
  NoContentLabel,
  NoContentLabelWrapper,
  InputWrapper,
  TitleAndSupplierLabelsWrapper,
  SkeletonWrapper,
  TooltipWrapper,
} from './DealerPanelModelsPage.styled';
import { useState } from 'react';
import {
  IDealerPanelSupplierDataFromNavigationState,
  useGetModelConfigurations,
  useGetSupplierDataFromNavigationState,
} from './hooks';
import { useTranslation } from 'react-i18next';
import ManageModelQuestionDocumentVisibilityModal from './ManageModelQuestionDocumentVisibilityModal/ManageModelQuestionDocumentVisibilityModal';
import { COLORS } from 'assets/styled';
import Icon from 'components/Icon/Icon';
import BackButton from 'components/BackButton/BackButton';
import { MagnifyingGlass, Question } from '@phosphor-icons/react';
import { RoutesConfig } from 'navigation/routes';
import { useNavigateWithQuery } from 'utils/hooks/useNavigateWithQuery';
import { IDealerPanelCategory } from 'types/Category.types';
import DealerPanelCategory from './DealerPanelCategory/DealerPanelCategory';
import Tooltip from 'components/Tooltip/Tooltip';
import { SelectVATAmountModal } from './SelectVATAmountModal/SelectVATAmountModal';
import { DealerPanelFormulaTooltipContent } from './DealerPanelFormulaTooltipContent/DealerPanelFormulaTooltipContent';
import CategoryListSkeleton from './CategoryListSkeleton';
import Input from 'components/Input/Input';
import { useDebounce } from 'utils/hooks/useDebounce';
import { useDispatch } from 'react-redux';
import { setFixedAmountCurrency } from 'store/SubmitConfiguration/actions/submitConfiguration';

export const DealerPanelModelsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigateWithQuery();
  const { t } = useTranslation();
  const [searchBy, setSearchBy] = useState<string>('');
  const [modelIdForTest, setModelIdForTest] = useState<number | null>(null);
  const debouncedSearchBy = useDebounce(searchBy);
  const {
    supplierId,
    supplierHasSystemIntegration,
  }: IDealerPanelSupplierDataFromNavigationState =
    useGetSupplierDataFromNavigationState();

  const { data, isLoading } = useGetModelConfigurations(
    supplierId,
    debouncedSearchBy
  );
  const [existingConfigurationId, setExistingConfigurationId] = useState<
    number | null
  >(null);

  const [manageVisibilityModelId, setManageVisibilityModelId] = useState<
    number | null
  >(null);

  dispatch(setFixedAmountCurrency(data?.fixed_amount_currency));

  const handleOnClick = () => {
    navigate(RoutesConfig.Suppliers.fullPath);
  };

  const renderLoader = () => {
    return (
      <SkeletonWrapper>
        {Array.from({ length: 5 }, (_, index) => (
          <CategoryListSkeleton
            key={`skeleton-row-${index}`}
            index={index}
            withoutLine={2 === index}
          />
        ))}
      </SkeletonWrapper>
    );
  };

  return (
    <Container>
      <BackWrapper>
        <BackButton destination={t('Suppliers')} onClick={handleOnClick} />
      </BackWrapper>
      <RowPaddingBottom>
        <TitleAndSupplierLabelsWrapper>
          <Title>{t('Models')}</Title>
          <SupplierCompanyName>
            {data?.company_name ? `(${data.company_name})` : ''}
          </SupplierCompanyName>
        </TitleAndSupplierLabelsWrapper>
        <InputWrapper>
          <TooltipWrapper>
            <Tooltip content={<DealerPanelFormulaTooltipContent />}>
              <Icon
                svg={Question}
                color={COLORS.GREEN}
                size={26}
                paddingLeft="5rem"
              />
            </Tooltip>
          </TooltipWrapper>
          <Input
            icon={MagnifyingGlass}
            placeholder={t('Search')}
            width={'100%'}
            height="41rem"
            value={searchBy}
            onChange={(e) => setSearchBy(e.target.value)}
            wrapperStyles={{ width: '100%' }}
          />
        </InputWrapper>
      </RowPaddingBottom>
      {data?.configurations?.length ? (
        <CategoriesContainer>
          {data?.configurations?.map(
            (category: IDealerPanelCategory, index: number) => (
              <DealerPanelCategory
                key={index}
                category={category}
                withoutLine={data.configurations.length - 1 === index}
                setManageVisibilityModelId={setManageVisibilityModelId}
                onTestClick={(
                  id: number,
                  existingConfigurationId: number | null
                ) => {
                  setModelIdForTest(id);
                  setExistingConfigurationId(existingConfigurationId);
                }}
                supplierHasSystemIntegration={supplierHasSystemIntegration}
                extraPriceCurrency={data?.extra_price_question_currency}
                fixedAmountCurrency={data?.fixed_amount_currency}
              />
            )
          )}
        </CategoriesContainer>
      ) : isLoading ? (
        renderLoader()
      ) : (
        <NoContentLabelWrapper>
          {!isLoading && (
            <NoContentLabel>
              {t('No models found for this supplier')}
            </NoContentLabel>
          )}
        </NoContentLabelWrapper>
      )}
      <ManageModelQuestionDocumentVisibilityModal
        manageVisibilityModelId={manageVisibilityModelId}
        setManageVisibilityModelId={setManageVisibilityModelId}
      />
      <SelectVATAmountModal
        modelIdForTest={modelIdForTest}
        setModelIdForTest={setModelIdForTest}
        supplierHasSystemIntegration={supplierHasSystemIntegration}
        existingConfigurationId={existingConfigurationId}
      />
    </Container>
  );
};
